import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { api } from "../../lib/api.js";
import { useAuth } from "../../lib/auth-context.js";
import { KpiTile } from "../../components/kpi-tile.js";
import { ActivityChart } from "../../components/activity-chart.js";

interface KpisResponse {
  kpis: {
    dmsIn:                { current: number; previous: number };
    repliesSent:          { current: number; previous: number };
    newContacts:          { current: number; previous: number };
    activeConversations:  { current: number; previous: null };
  };
  chConfigured: boolean;
  chError?: boolean;
}

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardHome,
});

function DashboardHome() {
  const { activeIgAccount } = useAuth();
  const [days, setDays] = useState<7 | 14 | 30>(7);
  const [data, setData] = useState<KpisResponse | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!activeIgAccount) return;
    setData(null);
    setLoadError(false);
    const params = new URLSearchParams({
      ig_account_id: activeIgAccount.id,
      days: String(days),
    });
    void api
      .get<KpisResponse>(`/analytics/dashboard-kpis?${params}`)
      .then((r) => setData(r.data))
      .catch(() => setLoadError(true));
  }, [activeIgAccount?.id, days]);

  if (!activeIgAccount) return <EmptyHome />;

  return (
    <div style={{ maxWidth: 1080 }}>
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 18,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 26,
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            letterSpacing: "-0.02em",
          }}
        >
          Home <span style={{ color: "var(--text-muted)" }}>·</span> @{activeIgAccount.igUsername}
        </h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value) as 7 | 14 | 30)}
          style={{
            border: "var(--border-soft)",
            borderRadius: 4,
            background: "var(--bg-surface)",
            padding: "6px 10px",
            fontSize: 13,
            fontFamily: "var(--font-display)",
            fontWeight: 600,
          }}
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
      </header>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 22,
        }}
      >
        <KpiTile
          label={`DMs in · ${days}d`}
          value={data?.kpis.dmsIn.current ?? null}
          previous={data?.kpis.dmsIn.previous ?? null}
          loading={!data && !loadError}
        />
        <KpiTile
          label={`Replies sent · ${days}d`}
          value={data?.kpis.repliesSent.current ?? null}
          previous={data?.kpis.repliesSent.previous ?? null}
          loading={!data && !loadError}
        />
        <KpiTile
          label={`New contacts · ${days}d`}
          value={data?.kpis.newContacts.current ?? null}
          previous={data?.kpis.newContacts.previous ?? null}
          loading={!data && !loadError}
        />
        <KpiTile
          label="Active conversations"
          value={data?.kpis.activeConversations.current ?? null}
          previous={null}
          loading={!data && !loadError}
        />
      </section>

      {loadError && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Couldn't load metrics. Retrying on next refresh.
        </div>
      )}

      <section style={{ marginBottom: 26 }}>
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: 14,
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: "var(--text-muted)",
            fontWeight: 600,
          }}
        >
          Activity (last 30 days)
        </h2>
        <ActivityChart igAccountId={activeIgAccount.id} days={30} />
      </section>

      <section>
        <h2
          style={{
            margin: "0 0 10px",
            fontSize: 14,
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: "var(--text-muted)",
            fontWeight: 600,
          }}
        >
          Quick actions
        </h2>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <QuickAction to="/inbox" label="Open inbox →" />
          <QuickAction to="/broadcasts/new" label="Send broadcast →" />
          <QuickAction to="/settings/auto-replies" label="Edit auto-replies →" />
        </div>
      </section>
    </div>
  );
}

function QuickAction({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      style={{
        padding: "10px 14px",
        background: "var(--bg-surface)",
        border: "var(--border-soft)",
        borderRadius: 4,
        boxShadow: "var(--shadow-soft-sm)",
        fontFamily: "var(--font-display)",
        fontWeight: 600,
        fontSize: 13,
        textDecoration: "none",
        color: "var(--ink)",
      }}
    >
      {label}
    </Link>
  );
}

function EmptyHome() {
  const API_URL = import.meta.env.VITE_API_URL ?? "";
  return (
    <div className="soft-card" style={{ maxWidth: 560, margin: "32px auto", padding: 32 }}>
      <h1
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: 32,
          letterSpacing: "-0.02em",
        }}
      >
        Connect Instagram
      </h1>
      <p style={{ margin: "10px 0 24px", color: "var(--text-muted)", maxWidth: 420 }}>
        Add an Instagram Business account so we can show your messages, contacts, and reply
        activity here.
      </p>
      <a
        href={`${API_URL}/auth/ig/start`}
        style={{
          display: "inline-block",
          padding: "12px 18px",
          background: "var(--accent)",
          border: "var(--border-soft)",
          borderRadius: 4,
          boxShadow: "var(--shadow-soft)",
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: 13,
          textDecoration: "none",
          color: "var(--ink)",
        }}
      >
        Link Instagram
      </a>
    </div>
  );
}
